import React, { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Quote } from 'lucide-react';
import { founderContent } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';

gsap.registerPlugin(ScrollTrigger);

const FounderSection = () => {
  const sectionRef = useRef(null);
  const { language } = useLanguage();
  const t = translations[language];

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo('.founder-image',
        { opacity: 0, x: -100, scale: 0.8 },
        {
          opacity: 1,
          x: 0,
          scale: 1,
          duration: 1.2,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 70%',
          }
        }
      );

      gsap.fromTo('.founder-content',
        { opacity: 0, x: 100 },
        {
          opacity: 1,
          x: 0,
          duration: 1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 70%',
          }
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, [language]);

  return (
    <section ref={sectionRef} className="py-20 lg:py-32 bg-white dark:bg-black transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="founder-image relative">
            <div className="relative">
              <div className="absolute -top-6 -left-6 w-32 h-32 bg-red-100 dark:bg-red-900/10 rounded-full z-0"></div>
              <div className="absolute -bottom-6 -right-6 w-24 h-24 bg-yellow-100 dark:bg-yellow-900/10 rounded-full z-0"></div>

              <div className="relative z-10 rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={founderContent.image}
                  alt={founderContent.name}
                  className="w-full h-auto object-cover aspect-[4/5]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>

                <div className="absolute bottom-6 left-6 right-6">
                  <div className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm rounded-xl p-4">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white" style={{ fontFamily: "'Poppins', sans-serif" }}>
                      {founderContent.name}
                    </h3>
                    <p className={`font-medium text-sm md:text-base ${language === 'mr' ? 'leading-[1.4]' : 'leading-tight'}`} style={{ fontFamily: "'Poppins', sans-serif" }}>
                      <span className="text-red-600 dark:text-red-500 block">{founderContent.title.split('\n')[0]}</span>
                      <span className="text-green-600 dark:text-green-400 block">{founderContent.title.split('\n')[1]}</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="founder-content">
            <span className="inline-block bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 px-4 py-2 rounded-full text-sm font-semibold mb-6" style={{ fontFamily: "'Poppins', sans-serif" }}>
              {t.founder.badge}
            </span>

            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-8" style={{ fontFamily: "'Poppins', sans-serif" }}>
              {t.founder.title} <span className="text-red-600">{t.founder.highlight}</span>
            </h2>

            <div className="relative mb-8">
              <Quote className="absolute -top-4 -left-4 w-12 h-12 text-red-100 dark:text-red-900/50" />
              <p className="text-lg md:text-xl text-gray-600 dark:text-gray-300 leading-relaxed italic pl-8" style={{ fontFamily: "'Poppins', sans-serif" }}>
                {t.founder.message}
              </p>
            </div>

            <div className="bg-gradient-to-r from-red-50 to-yellow-50 dark:from-red-900/20 dark:to-yellow-900/20 rounded-xl p-6 border-l-4 border-red-500">
              <p className="text-lg font-semibold text-gray-800 dark:text-gray-100" style={{ fontFamily: "'Poppins', sans-serif" }}>
                "{t.founder.quote}"
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2" style={{ fontFamily: "'Poppins', sans-serif" }}>
                — {founderContent.name}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FounderSection;
